import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
} from "@coreui/react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { getItem } from "../api/generics";

const periodOptions = [
  { value: "day", label: "Día" },
  { value: "week", label: "Semana" },
  { value: "month", label: "Mes" },
  { value: "year", label: "Año" },
];

const demoOptions = [
  { value: "production", label: "Producción" },
  { value: "demo", label: "Demo" },
  { value: "test", label: "Prueba" },
  { value: "all", label: "Todos" },
];

const StatsDashboardPage: React.FC = () => {
  const [period, setPeriod] = useState<string>("month");
  const [rankingPeriod, setRankingPeriod] = useState<string>("month");
  const [granularity, setGranularity] = useState<"day" | "week" | "month">(
    "day",
  );
  const [demo, setDemo] = useState<string>("production");
  const [docStats, setDocStats] = useState<any>(null);
  const [histogram, setHistogram] = useState<any[]>([]);
  const [taxPayerSummary, setTaxPayerSummary] = useState<any>(null);
  const [ranking, setRanking] = useState<any[]>([]);
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingHistogram, setLoadingHistogram] = useState(false);
  const [loadingRanking, setLoadingRanking] = useState(false);

  const loadStats = async (p: string, d: string) => {
    setLoadingStats(true);
    try {
      const params = new Map<string, string | undefined>([
        ["period", p],
        ["demo", d],
      ]);
      const [statsResp, summaryResp] = await Promise.all([
        getItem<any>("/admin-stats/documents/", params),
        getItem<any>("/admin-stats/tax-payers/", params),
      ]);

      if (statsResp.status === "success" || statsResp.status === undefined) {
        setDocStats(statsResp.data || statsResp);
      } else {
        setDocStats(null);
      }
      if (
        summaryResp.status === "success" ||
        summaryResp.status === undefined
      ) {
        setTaxPayerSummary(summaryResp.data || summaryResp);
      } else {
        setTaxPayerSummary(null);
      }
    } finally {
      setLoadingStats(false);
    }
  };

  const loadRanking = async (p: string, d: string) => {
    setLoadingRanking(true);
    try {
      const params = new Map<string, string | undefined>([
        ["period", p],
        ["demo", d],
      ]);
      const resp = await getItem<any>(
        "/admin-stats/tax-payers/ranking/",
        params,
      );
      if (resp.status === "success" || resp.status === undefined) {
        const raw = resp.data || resp;
        setRanking(Array.isArray(raw) ? raw : raw?.data || []);
      } else {
        setRanking([]);
      }
    } finally {
      setLoadingRanking(false);
    }
  };

  const loadHistogram = async (g: "day" | "week" | "month", d: string) => {
    setLoadingHistogram(true);
    try {
      const params = new Map<string, string | undefined>([
        ["granularity", g],
        ["demo", d],
      ]);
      const resp = await getItem<any>(
        "/admin-stats/documents/histogram/",
        params,
      );
      if (resp.status === "success" || resp.status === undefined) {
        const raw = resp.data || resp;
        setHistogram(Array.isArray(raw) ? raw : raw?.data || []);
      } else {
        setHistogram([]);
      }
    } finally {
      setLoadingHistogram(false);
    }
  };

  useEffect(() => {
    loadStats(period, demo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, demo]);

  useEffect(() => {
    loadRanking(rankingPeriod, demo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rankingPeriod, demo]);

  useEffect(() => {
    loadHistogram(granularity, demo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [granularity, demo]);

  const MONTHS_ES = [
    "Ene",
    "Feb",
    "Mar",
    "Abr",
    "May",
    "Jun",
    "Jul",
    "Ago",
    "Sep",
    "Oct",
    "Nov",
    "Dic",
  ];

  const formatPeriodLabel = (periodStr: string) => {
    if (granularity === "month") {
      // "YYYY-MM" → "Abr 25"
      const [year, month] = periodStr.split("-");
      return `${MONTHS_ES[parseInt(month, 10) - 1]} ${String(year).slice(2)}`;
    }
    if (granularity === "week") {
      // "YYYY-MM-DD" (Monday) → "7/4"
      const d = new Date(periodStr + "T00:00:00");
      return `${d.getDate()}/${d.getMonth() + 1}`;
    }
    // day: "YYYY-MM-DD" → "15/4"
    const d = new Date(periodStr + "T00:00:00");
    return `${d.getDate()}/${d.getMonth() + 1}`;
  };

  const granularityTitle: Record<string, string> = {
    day: "Últimos 30 días",
    week: "Últimas 12 semanas",
    month: "Últimos 12 meses",
  };

  return (
    <div>
      <CCard>
        <CCardBody className="d-flex flex-wrap align-items-center justify-content-between">
          <h1 className="mb-0 mr-3">Estadísticas Globales</h1>
          {/* On mobile the group wraps onto its own line, so it needs the same
              left edge as the title instead of a hardcoded right margin. */}
          <CButtonGroup size="sm" className="mt-2 mt-sm-0">
            {demoOptions.map((opt) => (
              <CButton
                key={opt.value}
                color={demo === opt.value ? "primary" : "secondary"}
                onClick={() => setDemo(opt.value)}
                disabled={loadingStats || loadingHistogram || loadingRanking}
              >
                {opt.label}
              </CButton>
            ))}
          </CButtonGroup>
        </CCardBody>
      </CCard>

      {/* Document stats */}
      <CCard>
        <CCardHeader>
          <strong>Estadísticas de Documentos</strong>
          <div className="card-header-actions">
            <CButtonGroup size="sm">
              {periodOptions.map((opt) => (
                <CButton
                  key={opt.value}
                  color={period === opt.value ? "primary" : "secondary"}
                  onClick={() => setPeriod(opt.value)}
                  disabled={loadingStats}
                >
                  {opt.label}
                </CButton>
              ))}
            </CButtonGroup>
          </div>
        </CCardHeader>
        <CCardBody>
          <div style={{ position: "relative" }}>
            {loadingStats && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "var(--theme-loading-overlay)",
                }}
              >
                <Spinner animation="border" variant="primary" />
              </div>
            )}
            {docStats ? (
              <>
                <CRow>
                  <CCol sm="6" lg="4">
                    <CCard className="text-white bg-primary">
                      <CCardBody className="d-flex align-items-center justify-content-between">
                        <div>
                          <div className="h4 mb-0">
                            {String(docStats.counts?.invoices || 0)}
                          </div>
                          <div className="small">Facturas</div>
                        </div>
                      </CCardBody>
                    </CCard>
                  </CCol>

                  <CCol sm="6" lg="4">
                    <CCard className="text-white bg-info">
                      <CCardBody>
                        <div className="h4 mb-0">
                          {String(docStats.counts?.credit_notes || 0)}
                        </div>
                        <div className="small">Notas de Crédito</div>
                      </CCardBody>
                    </CCard>
                  </CCol>

                  <CCol sm="6" lg="4">
                    <CCard className="text-dark bg-warning">
                      <CCardBody>
                        <div className="h4 mb-0">
                          {String(docStats.counts?.debit_notes || 0)}
                        </div>
                        <div className="small">Notas de Débito</div>
                      </CCardBody>
                    </CCard>
                  </CCol>

                  <CCol sm="6" lg="4">
                    <CCard className="text-white bg-success">
                      <CCardBody>
                        <div className="h4 mb-0">
                          {String(docStats.counts?.remission_notes || 0)}
                        </div>
                        <div className="small">Notas de Remisión</div>
                      </CCardBody>
                    </CCard>
                  </CCol>

                  <CCol sm="6" lg="4">
                    <CCard className="text-white bg-dark">
                      <CCardBody>
                        <div className="h4 mb-0">
                          {String(docStats.counts?.auto_invoices || 0)}
                        </div>
                        <div className="small">Autofacturas</div>
                      </CCardBody>
                    </CCard>
                  </CCol>

                  <CCol sm="6" lg="4">
                    <CCard className="text-white bg-danger">
                      <CCardBody>
                        <div className="h4 mb-0">
                          {String(docStats.counts?.receipts || 0)}
                        </div>
                        <div className="small">Recibos</div>
                      </CCardBody>
                    </CCard>
                  </CCol>
                </CRow>

                <CRow className="mt-4">
                  <CCol sm="4">
                    <CCard className="bg-primary text-white">
                      <CCardBody>
                        <h5 className="mb-0">
                          Total DES:{" "}
                          <strong>{docStats.totalDesCount || 0}</strong>
                        </h5>
                      </CCardBody>
                    </CCard>
                  </CCol>
                  <CCol sm="4">
                    <CCard className="bg-danger text-white">
                      <CCardBody>
                        <h5 className="mb-0">
                          Total Recibos:{" "}
                          <strong>{docStats.counts?.receipts || 0}</strong>
                        </h5>
                      </CCardBody>
                    </CCard>
                  </CCol>
                  <CCol sm="4">
                    <CCard className="bg-dark text-white">
                      <CCardBody>
                        <h5 className="mb-0">
                          Total General:{" "}
                          <strong>{docStats.totalCount || 0}</strong>
                        </h5>
                      </CCardBody>
                    </CCard>
                  </CCol>
                </CRow>
              </>
            ) : (
              <div
                className="text-center text-muted py-4"
                style={{ minHeight: 180 }}
              >
                {!loadingStats && "Sin datos."}
              </div>
            )}
          </div>
        </CCardBody>
      </CCard>

      {/* Histogram */}
      <CCard>
        <CCardHeader>
          <strong>Actividad — {granularityTitle[granularity]}</strong>
          <span className="text-muted ml-2 small">
            (DES + Recibos — comparable con "Total General")
          </span>
          <div className="card-header-actions">
            <CButtonGroup size="sm">
              <CButton
                color={granularity === "day" ? "primary" : "secondary"}
                onClick={() => setGranularity("day")}
                disabled={loadingHistogram}
              >
                Días
              </CButton>
              <CButton
                color={granularity === "week" ? "primary" : "secondary"}
                onClick={() => setGranularity("week")}
                disabled={loadingHistogram}
              >
                Semanas
              </CButton>
              <CButton
                color={granularity === "month" ? "primary" : "secondary"}
                onClick={() => setGranularity("month")}
                disabled={loadingHistogram}
              >
                Meses
              </CButton>
            </CButtonGroup>
          </div>
        </CCardHeader>
        <CCardBody>
          <div style={{ position: "relative", minHeight: 220 }}>
            {loadingHistogram && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "var(--theme-loading-overlay)",
                }}
              >
                <Spinner animation="border" variant="primary" />
              </div>
            )}
            {histogram.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={histogram.map((d) => ({
                    ...d,
                    label: formatPeriodLabel(d.period),
                  }))}
                  margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11 }}
                    interval={granularity === "day" ? 2 : 0}
                  />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(value) => [value, "Documentos"]}
                    labelFormatter={(label: string) => label}
                  />
                  <Bar dataKey="count" fill="#321fdb" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div
                className="d-flex align-items-center justify-content-center h-100"
                style={{ minHeight: 220 }}
              >
                {!loadingHistogram && (
                  <span className="text-muted">
                    Sin datos para el período seleccionado.
                  </span>
                )}
              </div>
            )}
          </div>
        </CCardBody>
      </CCard>

      {/* Taxpayer summary */}
      <CCard>
        <CCardHeader>
          <strong>Contribuyentes</strong>
        </CCardHeader>
        <CCardBody>
          {taxPayerSummary ? (
            <CRow>
              <CCol sm="6" lg="3">
                <CCard className="text-white bg-primary">
                  <CCardBody className="text-center">
                    <div className="h2 mb-0">{taxPayerSummary.total || 0}</div>
                    <div className="small">Total</div>
                  </CCardBody>
                </CCard>
              </CCol>
              <CCol sm="6" lg="3">
                <CCard className="text-white bg-info">
                  <CCardBody className="text-center">
                    <div className="h2 mb-0">
                      {taxPayerSummary.production || 0}
                    </div>
                    <div className="small">Productivos</div>
                  </CCardBody>
                </CCard>
              </CCol>
              <CCol sm="6" lg="3">
                <CCard className="text-dark bg-warning">
                  <CCardBody className="text-center">
                    <div className="h2 mb-0">{taxPayerSummary.demos || 0}</div>
                    <div className="small">Demos</div>
                  </CCardBody>
                </CCard>
              </CCol>
              <CCol sm="6" lg="3">
                <CCard className="text-dark bg-warning">
                  <CCardBody className="text-center">
                    <div className="h2 mb-0">{taxPayerSummary.test || 0}</div>
                    <div className="small">Prueba</div>
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>
          ) : (
            <div className="text-center text-muted py-4">
              Cargando contribuyentes...
            </div>
          )}
        </CCardBody>
      </CCard>

      {/* Ranking */}
      <CCard>
        <CCardHeader>
          <strong>Top 10 Contribuyentes por Documentos</strong>
          <div className="card-header-actions">
            <CButtonGroup size="sm">
              {periodOptions.map((opt) => (
                <CButton
                  key={opt.value}
                  color={rankingPeriod === opt.value ? "primary" : "secondary"}
                  onClick={() => setRankingPeriod(opt.value)}
                  disabled={loadingRanking}
                >
                  {opt.label}
                </CButton>
              ))}
            </CButtonGroup>
          </div>
        </CCardHeader>
        <CCardBody>
          <div style={{ position: "relative", minHeight: 120 }}>
            {loadingRanking && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "var(--theme-loading-overlay)",
                }}
              >
                <Spinner animation="border" variant="primary" />
              </div>
            )}
            {ranking.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-sm table-hover mb-0">
                  <thead>
                    <tr>
                      <th className="text-nowrap">#</th>
                      <th style={{ minWidth: 140 }}>Contribuyente</th>
                      <th className="text-nowrap">RUC</th>
                      <th className="text-nowrap">Tipo</th>
                      <th className="text-nowrap text-right">Documentos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ranking.map((r: any) => (
                      <tr key={r.taxPayerId}>
                        <td className="text-nowrap">{r.rank}</td>
                        <td className="text-break">{r.socialName}</td>
                        <td className="text-nowrap">{r.ruc}</td>
                        <td className="text-nowrap">
                          {r.isDemo ? (
                            <span className="badge badge-warning">Demo</span>
                          ) : (
                            <span className="badge badge-success">
                              Productivo
                            </span>
                          )}
                        </td>
                        <td className="text-nowrap text-right">
                          <strong>{r.count}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center text-muted py-4">
                {!loadingRanking && "Sin datos para el período seleccionado."}
              </div>
            )}
          </div>
        </CCardBody>
      </CCard>
    </div>
  );
};

export default StatsDashboardPage;
